import { ChangeEvent, FC } from 'react';
import { format, isValid } from 'date-fns';

import { useTranslate } from 'hooks/useTranslate';
import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import InputDate from 'modules/_shared/components/InputDate';
import InputNumber from 'modules/_shared/components/InputNumber';
import InputText from 'modules/_shared/components/InputText';
import { dateFormatInverted } from 'constants/formats';
import { PartnerExtraField, PartnerExtraFieldType } from 'types/Partner';

type Props = {
  extraFields: PartnerExtraField[];
  onChangeValue: (
    labelKey: string,
    value: string | number | boolean
  ) => void;
};

const getSortedExtraFields = (extraFields: PartnerExtraField[]) =>
  extraFields
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const getDateInputValue = (value: PartnerExtraField['value']): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  let dateValue: Date;

  if (value instanceof Date) {
    dateValue = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    dateValue = new Date(value);
  } else {
    return '';
  }

  if (!isValid(dateValue)) {
    return '';
  }

  return format(dateValue, dateFormatInverted);
};

const getNumberInputValue = (value: PartnerExtraField['value']): number => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  return 0;
};

const PersonalDataViewExtraFields: FC<Props> = ({
  extraFields,
  onChangeValue,
}) => {
  const { t } = useTranslate();

  if (!extraFields?.length) {
    return null;
  }

  const sortedFields = getSortedExtraFields(extraFields);

  const renderField = (field: PartnerExtraField) => {
    const fieldId = `partner-extra-field-${field.labelKey}`;
    const label = t(field.labelKey);

    switch (field.fieldType) {
      case PartnerExtraFieldType.STRING:
        return (
          <InputText
            id={fieldId}
            label={label}
            value={String(field.value ?? '')}
            onChange={(value) => onChangeValue(field.labelKey, value)}
          />
        );

      case PartnerExtraFieldType.NUMBER:
        return (
          <InputNumber
            id={fieldId}
            label={label}
            value={getNumberInputValue(field.value)}
            onChange={(value) => onChangeValue(field.labelKey, value)}
          />
        );

      case PartnerExtraFieldType.DATE:
        return (
          <InputDate
            id={fieldId}
            label={label}
            value={getDateInputValue(field.value)}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChangeValue(field.labelKey, event.target.value)
            }
          />
        );

      case PartnerExtraFieldType.BOOLEAN: {
        const isChecked = Boolean(field.value);

        return (
          <InputCheckbox
            id={fieldId}
            label={label}
            checked={isChecked}
            onChange={() => onChangeValue(field.labelKey, !isChecked)}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {sortedFields.map((field) => (
        <div key={field.labelKey} className="col-md-6">
          {renderField(field)}
        </div>
      ))}
    </>
  );
};

export default PersonalDataViewExtraFields;
